import * as React from "react"
import * as Yup from "yup"
import { CmsVendor } from "@modules/graphql/types"
import { useAvailableContentfulOrganizationsForUserQuery } from "@modules/cms/shared/queries.generated"
import { RadioButtonConnectedField, Text } from "gatsby-interface"
import Form from "@modules/form/components/Form"
import { composeIntegrationConfig } from "../utils"
import { ConnectCmsFormProps } from "../types"
import { ConnectCmsFormControls } from "./ConnectCmsFormControls"
import { useSetCmsIntegration } from "../hooks/useSetCmsIntegration"
import { CmsDataType } from "@modules/cms/shared/utils"
import Loading from "@modules/ui/components/Loading"
import {
  ConnectCmsFormLayout,
  loadingContainerCss,
} from "./ConnectCmsFormLayout"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import { cmsIntegration as cmsIntegrationText } from "@modules/locales/default.js"
import { commonMessageTags, FormattedMessage } from "@modules/locales"
import RefreshCmsAccountDataMessage from "@modules/cms/shared/components/RefreshCmsAccountDataMessage"

type FormValues = {
  dataSourceOrg: string
}

const validationSchema = Yup.object().shape<FormValues>({
  dataSourceOrg: Yup.string().required(
    cmsIntegrationText.messages.validationContentfulOrgRequired
  ),
})

export type ConnectContentfulOrganizationFormProps = ConnectCmsFormProps

export function ConnectContentfulOrganizationForm({
  siteId,
  siteName,
  onFetchError,
  onComplete,
  onCancel,
}: ConnectContentfulOrganizationFormProps) {
  const {
    data,
    loading,
    error,
    refetch,
  } = useAvailableContentfulOrganizationsForUserQuery({
    variables: {
      siteId,
    },
    onError: onFetchError,
    notifyOnNetworkStatusChange: true,
  })
  const [setCmsIntegration] = useSetCmsIntegration({
    siteId,
  })

  if (error) {
    return <ErrorAlert>{error.message}</ErrorAlert>
  }

  if (loading || data === undefined) {
    return (
      <div css={loadingContainerCss}>
        <Loading
          variant="baby"
          message={cmsIntegrationText.messages.loadingContentfulSpaces}
        />
      </div>
    )
  }

  const contentfulOrgs = data?.availableContentfulOrganizationsForUser ?? []
  type Option = { value: string; label: string }
  const orgOptions: Option[] = []
  let initialOrgId = contentfulOrgs[0]?.id ?? ``

  for (const org of contentfulOrgs) {
    orgOptions.push({
      value: org.id,
      label: org.name,
    })
    if (org.selected) {
      initialOrgId = org.id
    }
  }

  return (
    <Form<FormValues>
      initialValues={{
        dataSourceOrg: initialOrgId,
      }}
      validationSchema={validationSchema}
      onSubmit={values => {
        return setCmsIntegration({
          variables: {
            config: composeIntegrationConfig(
              CmsDataType.ContentfulOrganization,
              contentfulOrgs,
              values.dataSourceOrg
            ),
            siteId,
            vendor: CmsVendor.Contentful,
          },
        }).finally(onComplete)
      }}
      enableReinitialize
    >
      {({ isSubmitting }) => {
        return (
          <ConnectCmsFormLayout
            description={
              <FormattedMessage<"siteName", "strong">
                message={
                  cmsIntegrationText.messages.chooseContentfulOrgDataSource
                }
                values={{ siteName }}
                tags={commonMessageTags}
              />
            }
            controls={
              <ConnectCmsFormControls
                isSubmitting={isSubmitting}
                onCancel={onCancel}
              />
            }
            refreshMessage={
              <Text size="S">
                <RefreshCmsAccountDataMessage
                  message={
                    cmsIntegrationText.messages.refetchContentfulAccountData
                  }
                  refetch={refetch}
                />
              </Text>
            }
          >
            <RadioButtonConnectedField
              name="dataSourceOrg"
              label={cmsIntegrationText.labels.contentfulOrg}
              options={orgOptions}
              variant="framed"
            />
          </ConnectCmsFormLayout>
        )
      }}
    </Form>
  )
}
